import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  AppBar,
  Toolbar,
  Button,
  Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * PrivacyPolicy Component
 * Displays the Privacy Policy for Wyspur AI
 */
const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Wyspur brand colors matching LandingPage
  const wyspurColors = {
    darkNavy: '#0D0D21',
    navy: '#161C3E',
    blue: '#0095FF',
    text: '#FFFFFF',
    border: '#3C3C5A'
  };

  return (
    <Box sx={{ bgcolor: wyspurColors.darkNavy, minHeight: '100vh' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: wyspurColors.darkNavy,
          borderBottom: `1px solid ${wyspurColors.border}`
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: 1,
            px: { xs: 2, sm: 4, md: 6, lg: 8 }
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/logo-full-cropped.png"
            alt="Wyspur AI"
            sx={{
              height: { xs: '40px', md: '50px' },
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          />

          {/* Navigation Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                color: wyspurColors.text,
                borderColor: wyspurColors.blue,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(0, 149, 255, 0.1)',
                  borderColor: wyspurColors.blue
                }
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ color: wyspurColors.text }}>
          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            PRIVACY POLICY
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              textAlign: 'center',
              mb: 6,
              opacity: 0.8
            }}
          >
            Effective Date: November 2025
          </Typography>

          {/* Introduction */}
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            This Privacy Policy ("Policy") describes how SM Software & Consulting LLC, doing business as Wyspur AI ("Company," "we," "our," or "us"), collects, uses, discloses, and safeguards personal information of users ("you" or "your") in connection with the use of our website{' '}
            <MuiLink
              href="https://www.wyspurai.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: wyspurColors.blue,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              www.wyspurai.com
            </MuiLink>
            {' '}and related software services ("Services").
          </Typography>

          {/* Section 1 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            1. Information We Collect
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            We may collect personal information that you voluntarily provide, including your name, email address, business name, and payment details. Payment processing is handled through Stripe. We also collect analytical data through RB2B and technical information such as browser type, device identifiers, and usage patterns.
          </Typography>

          {/* Section 2 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            2. Use of Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            We use collected information to:
          </Typography>
          <Box component="ul" sx={{ mb: 2, pl: 4, lineHeight: 1.8 }}>
            <li>Provide, maintain, and improve our Services;</li>
            <li>Authenticate users and manage accounts;</li>
            <li>Process billing and subscription renewals;</li>
            <li>Communicate service-related notices;</li>
            <li>Monitor compliance and prevent unauthorized access.</li>
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            We do not sell or rent personal information to third parties.
          </Typography>

          {/* Section 3 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            3. Data Storage and Security
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            User data is securely stored using Supabase and encrypted both in transit and at rest. We employ reasonable administrative and technical measures to protect your information against unauthorized access, loss, misuse, or disclosure.
          </Typography>

          {/* Section 4 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            4. Tracking Technologies
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            We utilize RB2B to collect data regarding website interactions to enhance functionality and user experience. Users may disable tracking through their browser settings, though certain features may not function properly.
          </Typography>

          {/* Section 5 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            5. Data Retention and Deletion
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            We retain personal information for as long as necessary to provide Services or comply with legal obligations. Users may request account deletion by contacting{' '}
            <MuiLink
              href="mailto:sean@wyspurai.com"
              sx={{
                color: wyspurColors.blue,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              sean@wyspurai.com
            </MuiLink>
            , and we will delete the data within a reasonable period unless retention is legally required.
          </Typography>

          {/* Section 6 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            6. California Consumer Privacy Act (CCPA)
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            California residents may request access to, or deletion of, their personal data as provided under the CCPA by contacting us via the above email address.
          </Typography>

          {/* Section 7 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            7. Updates to the Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            We reserve the right to modify this Policy at any time. Continued use of the Services following updates constitutes acceptance of the revised Policy.
          </Typography>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: wyspurColors.navy,
          py: { xs: 3, md: 4 },
          borderTop: `1px solid ${wyspurColors.border}`,
          mt: 8
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography
            variant="body2"
            sx={{ color: wyspurColors.text, opacity: 0.6, textAlign: 'center' }}
          >
            © {new Date().getFullYear()} Wyspur AI. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
